import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TouchFriendlyInput, TouchFriendlyForm } from '../common/TouchFriendlyFormInputs';
import { useScreenSize } from '../reports/MobileReportInteractions';

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { isPortrait } = useScreenSize();
  
  const { register, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // Validate required fields
    if (!firstName || !lastName) {
      setFormError('First name and last name are required');
      return;
    }
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      const success = await register(firstName, lastName, email, password);
      if (success) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 bg-white p-6 sm:p-10 rounded-lg shadow-md">
        <div>
          <img 
            src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png" 
            alt="ROI Exteriors Logo" 
            className="mx-auto h-16"
          />
          <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            Create a new account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Register for the ROI Exteriors Marketing Analytics Portal
          </p>
        </div>
        
        {(error || formError) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{formError || error}</span>
          </div>
        )}
        
        <TouchFriendlyForm onSubmit={handleSubmit} className="mt-6">
          <div className="space-y-4">
            {/* Name fields with responsive spacing */}
            <div className="grid grid-cols-2 gap-3">
              <TouchFriendlyInput
                id="first-name"
                name="firstName"
                type="text"
                required
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
                showLabel={false}
              />
              
              <TouchFriendlyInput
                id="last-name"
                name="lastName"
                type="text"
                required
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label="Last Name"
                showLabel={false}
              />
            </div>
            
            <TouchFriendlyInput
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email address"
              showLabel={false}
            />
            
            <TouchFriendlyInput
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              placeholder="Password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              showLabel={false}
              error={password && password.length < 6 ? "Password must be at least 6 characters" : undefined}
            />
            
            <TouchFriendlyInput
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label="Confirm Password"
              showLabel={false}
              error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : undefined}
            />
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm">
              <Link 
                to="/login" 
                className={`font-medium text-blue-600 hover:text-blue-500 ${!isPortrait ? 'text-base' : ''}`}
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors min-h-[48px] duration-150"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
          
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Note: Registration access is limited to authorized ROI Exteriors personnel.</p>
          </div>
        </TouchFriendlyForm>
      </div>
    </div>
  );
};

export default Register;